<template>
  <div class="assessment-list-container">
    <div class="top-bar">
      <div class="top-bar-content">
        <div class="logo-section">
          <img
            src="/Teho_aly_logo.svg"
            alt="Teho-aly"
            class="logo"
            onerror="this.src='/Teho_aly_logo.png'"
          />
        </div>
        <h1 class="page-title">Arvioitavat työt</h1>
        <UserSection />
      </div>
    </div>

    <!-- Success Notice -->
    <div v-if="showSuccessNotice" class="success-notice">
      <div class="success-content">
        <span class="success-icon">✓</span>
        <span class="success-text">Arviointi lähetetty onnistuneesti</span>
      </div>
    </div>

    <div class="content-container">
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Ladataan arvioitavia töitä...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p>Virhe ladattaessa töitä: {{ error }}</p>
        <button @click="fetchAssessments" class="retry-button">
          Yritä uudelleen
        </button>
      </div>

      <div v-else-if="assessments.length === 0" class="empty-state">
        <h2>Ei arvioitavia töitä</h2>
        <p>Kaikki työt on arvioitu tai ei ole uusia töitä saatavilla.</p>
      </div>

      <div v-else>
        <!-- Filters and Controls -->
        <div class="controls-bar">
          <div class="filter-group">
            <label class="filter-label">Näytä:</label>
            <div class="filter-buttons">
              <button
                :class="[
                  'filter-button',
                  { active: currentFilter === 'tehtavat' },
                ]"
                @click="setFilter('tehtavat')"
              >
                Tehtävät ({{ tehtavatCount }})
              </button>
              <button
                :class="[
                  'filter-button',
                  { active: currentFilter === 'pikatehtavat' },
                ]"
                @click="setFilter('pikatehtavat')"
              >
                Pikatehtävät ({{ pikatehtavatCount }})
              </button>
              <button
                :class="['filter-button', { active: currentFilter === 'all' }]"
                @click="setFilter('all')"
              >
                Kaikki ({{ assessments.length }})
              </button>
            </div>
          </div>

          <div class="sort-group">
            <label class="sort-label">Järjestys:</label>
            <select v-model="currentSort" class="sort-select">
              <option value="oldest">Vanhin ensin</option>
              <option value="newest">Uusin ensin</option>
              <option value="course">Kurssin nimi</option>
              <option value="learning_unit">Tehtävän nimi</option>
            </select>
          </div>
        </div>

        <!-- Empty state for filtered results -->
        <div
          v-if="filteredAndSortedAssessments.length === 0"
          class="empty-state"
        >
          <h2>Ei {{ currentFilterText }}</h2>
          <p>Ei löytynyt yhtään työtä valituilla suodattimilla.</p>
        </div>

        <div v-else class="assessments-grid">
          <div
            v-for="assessment in filteredAndSortedAssessments"
            :key="assessment.id"
            class="assessment-card"
            @click="openAssessment(assessment.id)"
          >
            <div class="assessment-header">
              <h3 class="assessment-title">
                {{ assessment.learningUnit?.title || "Arvioitava työ" }}
              </h3>
              <span class="assessment-date">
                {{ formatDate(assessment.submittedTimestamp) }}
              </span>
            </div>

            <div class="assessment-details">
              <div class="detail-row">
                <span class="label">Opiskelija:</span>
                <span class="value">{{ assessment.userName }}</span>
              </div>
              <div class="detail-row">
                <span class="label">Kurssi:</span>
                <span class="value">{{ assessment.course?.title }}</span>
              </div>
              <div class="detail-row">
                <span class="label">Osio:</span>
                <span class="value">{{ assessment.section?.title }}</span>
              </div>
              <div v-if="assessment.answers?.length" class="detail-row">
                <span class="label">Vastauksia:</span>
                <span class="value">{{ assessment.answers.length }} kpl</span>
              </div>
            </div>

            <div class="assessment-actions">
              <button class="grade-button">
                {{ getButtonText(assessment.id) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import UserSection from "@/components/UserSection.vue";
import { useAuthStore } from "@/stores";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

interface AssessmentSummary {
  id: string;
  email: string;
  userName: string;
  submittedTimestamp: number;
  course?: {
    id: string;
    title: string;
  };
  section?: {
    id: string;
    title: string;
  };
  learningUnit?: {
    id: string;
    title: string;
    type: string;
  };
  answers?: Array<{
    blockId: string;
    blockType: string;
  }>;
}

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const assessments = ref<AssessmentSummary[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

// Filter and sort state
const currentFilter = ref<"tehtavat" | "pikatehtavat" | "all">("tehtavat");
const currentSort = ref<"oldest" | "newest" | "course" | "learning_unit">(
  "oldest",
);

// Success notice state
const showSuccessNotice = ref(false);

// Computed properties for filtering and sorting
const tehtavatAssessments = computed(() => {
  return assessments.value.filter(
    (assessment) => !assessment.learningUnit?.title?.startsWith("Pikatehtävä"),
  );
});

const pikatehtavatAssessments = computed(() => {
  return assessments.value.filter((assessment) =>
    assessment.learningUnit?.title?.startsWith("Pikatehtävä"),
  );
});

const tehtavatCount = computed(() => tehtavatAssessments.value.length);
const pikatehtavatCount = computed(() => pikatehtavatAssessments.value.length);

const currentFilterText = computed(() => {
  switch (currentFilter.value) {
    case "tehtavat":
      return "tehtäviä";
    case "pikatehtavat":
      return "pikatehtäviä";
    case "all":
      return "töitä";
    default:
      return "töitä";
  }
});

const filteredAssessments = computed(() => {
  switch (currentFilter.value) {
    case "tehtavat":
      return tehtavatAssessments.value;
    case "pikatehtavat":
      return pikatehtavatAssessments.value;
    case "all":
      return assessments.value;
    default:
      return tehtavatAssessments.value;
  }
});

const filteredAndSortedAssessments = computed(() => {
  const filtered = [...filteredAssessments.value];

  switch (currentSort.value) {
    case "oldest":
      return filtered.sort(
        (a, b) => a.submittedTimestamp - b.submittedTimestamp,
      );
    case "newest":
      return filtered.sort(
        (a, b) => b.submittedTimestamp - a.submittedTimestamp,
      );
    case "course":
      return filtered.sort((a, b) => {
        const courseA = a.course?.title || "";
        const courseB = b.course?.title || "";
        return courseA.localeCompare(courseB);
      });
    case "learning_unit":
      return filtered.sort((a, b) => {
        const titleA = a.learningUnit?.title || "";
        const titleB = b.learningUnit?.title || "";
        return titleA.localeCompare(titleB);
      });
    default:
      return filtered.sort(
        (a, b) => a.submittedTimestamp - b.submittedTimestamp,
      );
  }
});

// Filter functions
const setFilter = (filter: "tehtavat" | "pikatehtavat" | "all") => {
  currentFilter.value = filter;
};

// Assessment functions
const fetchAssessments = async () => {
  loading.value = true;
  error.value = null;

  try {
    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) ||
      "http://localhost:3000/api";
    const response = await fetch(`${apiBaseUrl}/assessment_responses`, {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch assessments");
    }

    const data = (await response.json()) as {
      assessments: AssessmentSummary[];
    };
    assessments.value = data.assessments || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : "Unknown error";
    console.error("Error fetching assessments:", err);
  } finally {
    loading.value = false;
  }
};

const openAssessment = async (assessmentId: string) => {
  await router.push(`/grading/${assessmentId}`);
};

const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp * 1000); // Convert from seconds to milliseconds
  return date.toLocaleDateString("fi-FI", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Reactive state to track which assessments have saved state
const assessmentsWithState = ref<Set<string>>(new Set());

// Helper function to check if assessment has saved state (using reactive state)
const hasAssessmentState = (assessmentId: string): boolean => {
  return assessmentsWithState.value.has(assessmentId);
};

// Get button text based on whether assessment has saved state
const getButtonText = (assessmentId: string): string => {
  return hasAssessmentState(assessmentId)
    ? "Jatka arviointia"
    : "Aloita arviointi";
};

// Load assessment states from Pinia (with background refresh)
const loadAssessmentStates = async (forceRefresh = false) => {
  if (!authStore.currentProfile) {
    assessmentsWithState.value = new Set();
    return;
  }

  try {
    // Load immediately from cache for instant rendering
    const cachedStates = authStore.getCurrentProfileAssessmentList;
    if (cachedStates.length > 0 && !forceRefresh) {
      const assessmentIds = cachedStates.map((state) => state.assessment_id);
      assessmentsWithState.value = new Set(assessmentIds);
      console.log(
        `📊 Loaded ${assessmentIds.length} assessment states from cache`,
      );
    }

    // Load from API and update cache (background refresh)
    const apiStates = await authStore.loadAssessmentList(forceRefresh);
    const assessmentIds = apiStates.map((state) => state.assessment_id);
    assessmentsWithState.value = new Set(assessmentIds);

    console.log(`📊 Loaded ${assessmentIds.length} assessment states from API`);
  } catch (err) {
    console.warn("Failed to load assessment states:", err);
    // Fallback to empty set
    assessmentsWithState.value = new Set();
  }
};

// Watch for success query parameter
watch(
  () => route.query.submitted as string | undefined,
  (submitted: string | undefined) => {
    if (submitted === "true") {
      showSuccessNotice.value = true;

      // Auto-close after 4 seconds
      setTimeout(() => {
        showSuccessNotice.value = false;
      }, 4000);

      // Clear the query parameter
      void router.replace({ path: "/assessments" });
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await fetchAssessments();
  await loadAssessmentStates();
});
</script>

<style scoped>
.assessment-list-container {
  min-height: 100vh;
  background-color: #f0f2f5;
  padding-top: 60px;
}

.top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background-color: var(--color-bg-primary);
  border-bottom: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-sm);
  z-index: 1000;
  display: flex;
  align-items: center;
  padding: 0 var(--space-md);
}

.top-bar-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  /* padding: 0 var(--space-md); */
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.2rem;
  gap: var(--space-md);
  color: var(--color-text-primary);
  line-height: 1.2;
}

.logo-section {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.logo {
  height: 32px;
  width: auto;
  max-width: 100px;
}

.page-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

.content-container {
  max-width: 1200px;
  margin: var(--space-lg) auto;
}

/* Controls Bar */
.controls-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-lg);
  margin-bottom: var(--space-lg);
  padding: var(--space-md);
  background: white;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-sm);
}

.filter-group,
.sort-group {
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.filter-label,
.sort-label {
  font-weight: 500;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  white-space: nowrap;
}

.filter-buttons {
  display: flex;
  gap: var(--space-xs);
}

.filter-button {
  padding: var(--space-xs) var(--space-md);
  border: 1px solid var(--color-border-light);
  background: white;
  color: var(--color-text-secondary);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
}

.filter-button:hover {
  background: var(--color-bg-tertiary);
  border-color: var(--color-primary);
  color: var(--color-text-primary);
}

.filter-button.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.sort-select {
  padding: var(--space-xs) var(--space-md);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: white;
  color: var(--color-text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 150px;
}

.sort-select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.1);
}

.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: var(--space-xl);
}

.loading-spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--space-md);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.retry-button {
  padding: var(--space-sm) var(--space-md);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 500;
  margin-top: var(--space-md);
}

.retry-button:hover {
  background: var(--color-primary-dark);
}

.assessments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: var(--space-lg);
  margin-top: var(--space-lg);
}

.assessment-card {
  background: white;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-sm);
  padding: var(--space-lg);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.assessment-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
  border-color: var(--color-primary);
}

.assessment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-md);
}

.assessment-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  flex: 1;
}

.assessment-date {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  white-space: nowrap;
  margin-left: var(--space-md);
}

.assessment-details {
  margin-bottom: var(--space-lg);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-xs);
}

.label {
  font-weight: 500;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
}

.value {
  color: var(--color-text-primary);
  font-size: 0.875rem;
  text-align: right;
  flex: 1;
  margin-left: var(--space-md);
}

.assessment-actions {
  display: flex;
  justify-content: flex-end;
}

.grade-button {
  padding: var(--space-sm) var(--space-lg);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease-in-out;
}

.grade-button:hover {
  background: var(--color-primary-dark);
  transform: translateY(-1px);
}

/* Success Notice */
.success-notice {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1002;
  animation: slideDown 0.3s ease-out;
}

.success-content {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: var(--space-md) var(--space-lg);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-weight: 500;
}

.success-icon {
  font-size: 1.2rem;
  font-weight: bold;
}

.success-text {
  font-size: 0.9rem;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .controls-bar {
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-md);
  }

  .filter-group,
  .sort-group {
    justify-content: space-between;
  }

  .filter-buttons {
    flex: 1;
    justify-content: flex-end;
  }

  .sort-select {
    flex: 1;
    max-width: 200px;
  }

  .assessments-grid {
    grid-template-columns: 1fr;
    gap: var(--space-md);
  }

  .assessment-header {
    flex-direction: column;
    gap: var(--space-xs);
  }

  .assessment-date {
    margin-left: 0;
    align-self: flex-start;
  }

  .detail-row {
    flex-direction: column;
    gap: var(--space-xs);
  }

  .value {
    text-align: left;
    margin-left: 0;
  }
}
</style>
